import React, { useState, Fragment, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, BookOpen, Check } from 'lucide-react';
import { LessonResourceBlock } from '../../data/trainingCourse';
interface LessonResourcesProps {
  blocks: LessonResourceBlock[];
  label?: string;
}
// Matches emails, full http(s) URLs, and bare domains (e.g. examfx.com, Quizlet.com).
const LINK_REGEX = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})|(https?:\/\/[^\s]+)|(\b[a-zA-Z0-9-]+\.(?:com|org|net|io|co|gov|edu)\b)/g;
/** Turns raw text into React nodes with clickable links for URLs, domains, and emails. */
function linkify(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const regex = new RegExp(LINK_REGEX);
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    const [full, email, url, domain] = match;
    if (match.index > lastIndex) {
      nodes.push(<Fragment key={`t-${key++}`} data-id="element-6" data-mp="82ugtpsb7fqc">
          {text.slice(lastIndex, match.index)}
        </Fragment>);
    }
    if (email) {
      nodes.push(<a key={`l-${key++}`} href={`mailto:${email}`} className="text-blue-600 font-semibold hover:text-blue-700 underline underline-offset-2 break-words" data-id="element-7" data-mp="2rcvy3hpq89yk">
          {email}
        </a>);
    } else {
      const href = url ? url : `https://${domain}`;
      nodes.push(<a key={`l-${key++}`} href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold hover:text-blue-700 underline underline-offset-2 break-words" data-id="element-8" data-mp="361il85lli60c">
          {full}
        </a>);
    }
    lastIndex = match.index + full.length;
  }
  if (lastIndex < text.length) {
    nodes.push(<Fragment key={`t-${key++}`} data-id="element-9" data-mp="3oytl9sf1x3gu">{text.slice(lastIndex)}</Fragment>);
  }
  return nodes;
}
export function LessonResources({
  blocks,
  label = 'Resources & Steps'
}: LessonResourcesProps) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="w-full max-w-4xl mt-4 border border-slate-200 rounded-xl bg-white overflow-hidden" data-id="element-10" data-mp="7eth4vlgny59">
      <button type="button" onClick={() => setIsOpen(o => !o)} aria-expanded={isOpen} className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors" data-id="element-11" data-mp="2g8b2fjj2k8w">
        <span className="flex items-center gap-3 font-bold text-slate-900" data-id="element-12" data-mp="3kor3fqdbnblt">
          <BookOpen className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-13" data-mp="3fwftrvss4bg2" />
          {label}
        </span>
        <ChevronDown className={`w-5 h-5 text-slate-500 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-14" data-mp="3axd017i91rne" />
      </button>

      <AnimatePresence initial={false} data-id="element-15" data-mp="35wyp9hp9k3lo">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-16" data-mp="30z6yws49opjl">
            <div className="px-5 pb-6 pt-1 border-t border-slate-100 space-y-8" data-id="element-17" data-mp="1qfln3yd1l1jw">
              {blocks.map((block, i) => <div key={i} className="space-y-3" data-id="element-18" data-mp="ssv25c6qksvm" data-mpi={i}>
                  {block.heading && <h4 className="text-sm font-extrabold text-blue-700 uppercase tracking-wider" data-id="element-19" data-mp="xqeq53t8kk0r">
                      {block.heading}
                    </h4>}
                  {block.paragraphs?.map((p, pi) => <p key={pi} className="text-slate-600 leading-relaxed whitespace-pre-line" data-id="element-20" data-mp="17ikiwfrbb5h0">
                      {linkify(p)}
                    </p>)}
                  {block.steps && block.steps.length > 0 && <ol className="space-y-3" data-id="element-21" data-mp="2gq45lzrepn00">
                      {block.steps.map((step, si) => <li key={si} className="flex gap-3 text-slate-600 leading-relaxed" data-id="element-22" data-mp="2cm679r9wog80" data-mpi={si}>
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-sm font-bold flex items-center justify-center mt-0.5" data-id="element-23" data-mp="2lo5p2lsqvoc0">
                            {si + 1}
                          </span>
                          <span data-id="element-24" data-mp="2vguy7a0toq46">{linkify(step)}</span>
                        </li>)}
                    </ol>}
                  {block.bullets && block.bullets.length > 0 && <ul className="space-y-2.5" data-id="element-25" data-mp="1f0io482gnuef">
                      {block.bullets.map((bullet, bi) => <li key={bi} className="flex gap-3 text-slate-600 leading-relaxed" data-id="element-26" data-mp="1a1nueb2yglfj" data-mpi={bi}>
                          <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-27" data-mp="3lquagrywgbm3" />
                          <span data-id="element-28" data-mp="3qn2p8496q2sq">{linkify(bullet)}</span>
                        </li>)}
                    </ul>}
                  {block.images && block.images.length > 0 && <div className="flex flex-wrap gap-4" data-id="element-29" data-mp="3gvogr6z99sng">
                      {block.images.map((image, ii) => <img key={ii} src={image.src} alt={image.alt || 'Resource image'} className="w-full max-w-md rounded-xl border border-slate-200 shadow-sm" loading="lazy" data-id="element-30" data-mp="37x33nrcjz5u2" data-mpi={ii} />)}
                    </div>}
                </div>)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}